import React, { useState, useEffect } from 'react';
import { HiPlus, HiOutlineInformationCircle, HiExclamation, HiCheck, HiClock, HiX, HiOutlineX } from 'react-icons/hi';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import AppLoadingState, { LoadingSpinnerRing } from './common/AppLoadingState';
import { AppPageShell, Button, PageHeader } from './common/ui';

const BatchStatusIndicator = ({ status }) => {
  const total = status?.calls_requested || 0;
  const completed = status?.calls_completed?.length || 0;
  const failed = status?.calls_failed || 0;
  const inProgress = status?.calls_in_progress?.length || 0;
  const inRetry = status?.calls_in_retry?.length || 0;

  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">Progress</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="flex space-x-1 h-2 rounded-full overflow-hidden bg-gray-200">
        {/* Completed */}
        {completed > 0 && (
          <div 
            style={{ width: `${(completed/total) * 100}%` }}
            className="bg-green-500"
          />
        )}
        {/* In Progress */}
        {inProgress > 0 && (
          <div 
            style={{ width: `${(inProgress/total) * 100}%` }}
            className="bg-blue-500"
          />
        )}
        {/* In Retry */}
        {inRetry > 0 && (
          <div 
            style={{ width: `${(inRetry/total) * 100}%` }}
            className="bg-yellow-500"
          />
        )}
        {/* Failed */}
        {failed > 0 && (
          <div 
            style={{ width: `${(failed/total) * 100}%` }}
            className="bg-red-500"
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center space-x-2">
          <HiCheck className="w-4 h-4 text-green-500" />
          <span className="text-gray-600">Completed: {completed}</span>
        </div>
        <div className="flex items-center space-x-2">
          <HiClock className="w-4 h-4 text-blue-500" />
          <span className="text-gray-600">In Progress: {inProgress}</span>
        </div>
        <div className="flex items-center space-x-2">
          <HiExclamation className="w-4 h-4 text-yellow-500" />
          <span className="text-gray-600">In Retry: {inRetry}</span>
        </div>
        <div className="flex items-center space-x-2">
          <HiX className="w-4 h-4 text-red-500" />
          <span className="text-gray-600">Failed: {failed}</span>
        </div>
      </div>
    </div>
  );
};

const BatchDetailsModal = ({ isOpen, onClose, batchId }) => {
  const [details, setDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && batchId) {
      loadBatchData();
    }
  }, [isOpen, batchId]);

  const loadBatchData = async () => {
    setIsLoading(true);
    try {
      const detailsResponse = await api.getBatchDetails(batchId, { include_calls: true, include_transcripts: true });
      console.log('Batch Details Response:', detailsResponse);

      // Prefer listing calls by batch/campaign id. Logs from provider batches don't always include call IDs.
      const allCalls = await api.getCalls();
      const callDetails = Array.isArray(allCalls)
        ? allCalls.filter((c) => String(c.batch_id || '') === String(batchId) || String(c.campaign_id || '') === String(batchId))
        : [];

      setDetails({
        ...detailsResponse,
        calls: callDetails
      });
    } catch (error) {
      console.error('Error loading batch data:', error);
      toast.error('Failed to load batch details');
    } finally {
      setIsLoading(false);
    }
  };

  const renderCallData = (call) => {
    return (
      <tr key={call.call_id || call.c_id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            call.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {call.completed ? 'Completed' : 'In Progress'}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            <Link 
              to={`/app/calls/${call.call_id || call.c_id}`}
              className="text-blue-600 hover:text-blue-900"
            >
              {(call.call_id || call.c_id)?.substring(0, 8)}...
            </Link>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(call.call_id || call.c_id);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
              title="Copy Call ID"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.to || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.from || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.recording_url ? (
            <Link 
              to={`/app/calls/${call.call_id || call.c_id}?tab=recording`}
              className="text-blue-600 hover:text-blue-900"
            >
              View
            </Link>
          ) : '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.call_length ? `${(call.call_length * 60).toFixed(0)}s` : '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.answered_by || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.call_ended_by || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.transferred_to || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.batch_id || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500">
          {call.error_message || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {call.created_at ? new Date(call.created_at).toLocaleString() : '-'}
        </td>
      </tr>
    );
  };

  const renderAnalysisSummary = () => {
    if (!details?.analysis) return null;
    const { analysis: stats } = details;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Calls</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">{stats.total_calls}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Completed</div>
          <div className="mt-1 text-2xl font-semibold text-green-600">{stats.completed_calls}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">In Progress</div>
          <div className="mt-1 text-2xl font-semibold text-blue-600">{stats.in_progress_calls}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Avg Duration</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">{stats.call_lengths?.average || 0}s</div>
        </div>
      </div>
    );
  };

  const renderDurationDistribution = () => {
    if (!details?.analysis?.call_lengths?.summary) return null;
    const summary = details.analysis.call_lengths.summary;

    return (
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Call Duration Distribution</h3>
        <div className="space-y-2">
          {Object.entries(summary).map(([range, count]) => (
            <div key={range} className="flex items-center">
              <div className="w-24 text-sm text-gray-600">{range}</div>
              <div className="flex-1">
                <div className="relative h-4 bg-gray-100 rounded">
                  <div
                    className="absolute h-4 bg-blue-200 rounded"
                    style={{
                      width: `${(count / details.analysis.total_calls) * 100}%`
                    }}
                  />
                </div>
              </div>
              <div className="w-12 text-right text-sm text-gray-600">{count}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCallParams = () => {
    if (!details?.batch_params?.call_params) return null;
    const { call_params } = details.batch_params;

    return (
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        {Object.entries(call_params).map(([key, value]) => {
          // Skip rendering the base_prompt and call_data as they're shown elsewhere
          if (key === 'base_prompt' || key === 'call_data') return null;
          
          let displayValue = value;
          if (typeof value === 'boolean') {
            displayValue = value ? 'Yes' : 'No';
          } else if (typeof value === 'object') {
            displayValue = JSON.stringify(value);
          }

          return (
            <p key={key}>
              <span className="font-medium">
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
              </span>{' '}
              {displayValue}
            </p>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Campaign Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <HiOutlineX className="h-6 w-6" />
          </button>
        </div>

        {isLoading ? (
          <AppLoadingState layout="embed" showLabel={false} />
        ) : (
          <>
            <div className="border-b">
              <nav className="flex space-x-8 px-6">
                {['overview', 'calls'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 8rem)' }}>
              {activeTab === 'overview' && details && (
                <div className="space-y-6">
                  {renderAnalysisSummary()}
                  {renderDurationDistribution()}

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Campaign Information</h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <p><span className="font-medium">Label:</span> {details.batch_params.label}</p>
                        <p><span className="font-medium">Created:</span> {new Date(details.batch_params.created_at).toLocaleString()}</p>
                        <p><span className="font-medium">Status:</span> {details.status}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Call Parameters</h3>
                      {renderCallParams()}
                    </div>
                  </div>

                  {details.batch_params.base_prompt && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Prompt</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="whitespace-pre-wrap">{details.batch_params.base_prompt}</p>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {activeTab === 'calls' && details?.calls && (
                <div className="space-y-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Call ID</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recording</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Answered By</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Call Ended By</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transferred To</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch ID</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error Message</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {details.calls.map(renderCallData)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedErrors, setExpandedErrors] = useState({});
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setIsLoading(true);
      const response = await api.getBatches();
      setCampaigns(response.batches || []);
    } catch (error) {
      toast.error('Failed to load campaigns');
      console.error('Error loading campaigns:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    navigate('/app/campaigns/create');
  };

  const toggleErrorExpansion = (batchId) => {
    setExpandedErrors(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  if (isLoading) {
    return <AppLoadingState layout="section" showLabel={false} />;
  }

  return (
    <AppPageShell>
      <div className="space-y-6">
      <PageHeader
        eyebrow="Outbound"
        title="Campaigns"
        description="View batch progress, retries, and outcomes. Open a campaign to inspect per-call results and errors."
        actions={
          <Button onClick={handleCreateCampaign}>
            <HiPlus className="-ml-1 mr-2 h-5 w-5" />
            Create Campaign
          </Button>
        }
      />

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <HiOutlineInformationCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              View and manage your campaign batches. Each batch shows detailed progress and status information.
            </p>
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-12">
          <div className="flex flex-col items-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns yet</h3>
            <p className="mt-1 text-sm text-gray-500">Create a campaign to get started.</p>
            <div className="mt-6">
              <button
                onClick={handleCreateCampaign}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <HiPlus className="-ml-1 mr-2 h-5 w-5" />
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <ul className="divide-y divide-gray-200">
            {campaigns.map((campaign) => (
              <li 
                key={campaign.batch_id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedBatchId(campaign.batch_id);
                  setIsModalOpen(true);
                }}
              >
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-lg font-medium text-blue-600 truncate">
                          {campaign.label || 'Untitled Campaign'}
                        </h3>
                        <span className="text-xs text-gray-500">
                          Created {new Date(campaign.created_at).toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Campaign Details */}
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900 font-medium">Campaign Settings</p>
                          <div className="text-sm text-gray-600">
                            <p>Model: {campaign.call_params.model}</p>
                            <p>Language: {campaign.call_params.language}</p>
                            <p>Voice: {campaign.call_params.voice}</p>
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900 font-medium">Prompt/Dialog Flow</p>
                          <div className="text-sm text-gray-600">
                            {campaign.base_prompt ? (
                              <p className="line-clamp-2">{campaign.base_prompt}</p>
                            ) : campaign.call_params.pathway_id ? (
                              <p>Using Dialog Flow</p>
                            ) : (
                              <p className="text-gray-400">No prompt specified</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Batch Status */}
                      <div className="mt-4">
                        <BatchStatusIndicator status={campaign.batch_status} />
                      </div>

                      {/* Error Messages */}
                      {campaign.batch_status.calls_failed_errors?.length > 0 && (
                        <div className="mt-4">
                          <button
                            onClick={() => toggleErrorExpansion(campaign.batch_id)}
                            className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800"
                          >
                            <HiExclamation className="h-5 w-5" />
                            <span>{campaign.batch_status.calls_failed_errors.length} Error{campaign.batch_status.calls_failed_errors.length > 1 ? 's' : ''}</span>
                          </button>
                          
                          {expandedErrors[campaign.batch_id] && (
                            <div className="mt-2 p-3 bg-red-50 rounded-md">
                              <ul className="list-disc list-inside space-y-1">
                                {campaign.batch_status.calls_failed_errors.map((error, index) => (
                                  <li key={index} className="text-sm text-red-700">
                                    {error}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <BatchDetailsModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBatchId(null);
        }}
        batchId={selectedBatchId}
      />
      </div>
    </AppPageShell>
  );
};

export default Campaigns; 